import type {
  MenuBannerItem,
  SubheaderDetails,
} from '@bulknutrients/bulk-shared-lib/interfaces/Menu';

import { Button } from '@components/Button';
import {
  UIContext,
  type UIContextInterface,
} from '@components/Context/UIContext/UIContext';
import { LoginModalUncontrolled } from '@components/LoginForm/LoginModal';
import { CustomImage } from '@components/Media/Image/Image';
import {
  RewardsLink,
  TextLink,
} from '@components/Typography/TextLink/TextLink';
import { useCustomer } from '@hooks/customer/useCustomer';
import { useContext, useEffect, useRef } from 'react';

export const MenuHeader = ({
  bannerInfo,
  details,
}: {
  bannerInfo: MenuBannerItem | undefined;
  details: SubheaderDetails | undefined;
}) => {
  const { customer } = useCustomer();
  const scrollRef = useRef<null | HTMLDivElement>(null);
  const { menuBreadcrumbs } = useContext<UIContextInterface>(UIContext);

  useEffect(() => {
    // when navigation in menu changes, scroll to top of menu
    // ref is on the nav bar bc it's the top of the menu
    if (scrollRef?.current) scrollRef?.current.scrollIntoView();
  }, [menuBreadcrumbs]);

  return menuBreadcrumbs.length === 0 ? (
    <div className="w-auto scroll-mt-10 pb-5 pt-10" ref={scrollRef}>
      
      {customer ? (
        <div>
          <h2 className="pb-3 italic">
            Welcome back,&nbsp;
            {customer.firstName}
          </h2>
          <p className="mb-4">
            {customer.loyaltyLion
              ? `You've earned ${customer.loyaltyLion.points} reward points! `
              : ''}
            Learn how to <RewardsLink>redeem your points</RewardsLink>.
          </p>
        </div>
      ) : (
        <div>
          <h2 className="pb-3 italic">Hey there, Bulk fan!</h2>
          <p className="leading-1.25 sm:pb-3">
            <LoginModalUncontrolled
              trigger={
                <Button buttonStyle="text" className="inline">
                  Sign in
                </Button>
              }
              title={<>Login to your account</>}
            />{' '}
            or <TextLink href="/create-account">create an account</TextLink> to
            earn points with every purchase.
          </p>
        </div>
      )}
      {bannerInfo?.image && (
        <CustomImage
          image={{
            ...bannerInfo.image,
            link: bannerInfo.links[0].slug ?? bannerInfo.links[0].fallback,
          }}
          width={691}
          alt={bannerInfo.title}
        />
      )}
    </div>
  ) : (
    <div className="w-auto scroll-mt-10 pb-5 pt-15" ref={scrollRef}>
      <h2 className="italic">{menuBreadcrumbs[menuBreadcrumbs.length - 1]}</h2>
      {details?.description && (
        <p className="my-2 text-xs">{details.description}</p>
      )}
      {details?.link && (
        <TextLink className="text-xs" href={details.link.slug}>
          {details.link.title}
        </TextLink>
      )}
    </div>
  );
};
